import React from 'react';
import { getScoreColor } from '../../utils/styleUtils';
import { formatTaskName } from '../../utils/formatUtils';
import { getScoreKey } from '../../utils/dataUtils';

/**
 * Desktop table view for benchmark data
 * 
 * @param {Object} props - Component props
 * @param {Array} props.filteredData - Array of filtered data items
 * @param {Array} props.visibleModels - Array of currently visible models
 * @param {Object} props.sortConfig - Current sort configuration
 * @param {Function} props.handleSort - Function to handle sort changes
 * @param {Object} props.averageScores - Object containing average scores
 */
const DesktopTableView = ({ 
  filteredData, 
  visibleModels, 
  sortConfig, 
  handleSort,
  averageScores
}) => {
  // Helper function to determine if a model is one of the company's ASI models
  const isCompanyModel = (modelId) => {
    return modelId === 'asi1_mini' || modelId === 'asi1_mini_0225';
  };
  
  // Function to get the header class based on whether it's a company model
  const getModelHeaderClass = (model) => {
    if (isCompanyModel(model.id)) {
      return "px-2 py-3 text-center text-xs font-medium uppercase tracking-wider cursor-pointer whitespace-nowrap bg-blue-50 border-x-2 border-blue-400";
    }
    return "px-2 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer whitespace-nowrap";
  };
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white border">
        <thead className="bg-gray-100">
          <tr>
            {/* ID column header */}
            <th 
              className="sticky left-0 bg-gray-100 px-2 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer whitespace-nowrap z-10"
              onClick={() => handleSort('id')}
            >
              <div className="flex items-center">
                #
                {sortConfig.key === 'id' && (
                  <span className="ml-1">{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                )}
              </div>
            </th>
            
            {/* Task name column header */}
            <th 
              className="sticky left-8 bg-gray-100 px-2 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer whitespace-nowrap z-10"
              onClick={() => handleSort('task')}
            >
              <div className="flex items-center">
                Task
                {sortConfig.key === 'task' && (
                  <span className="ml-1">{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                )}
              </div>
            </th>
            
            {/* Model column headers */}
            {visibleModels.map((model) => (
              <th 
                key={model.id}
                className={getModelHeaderClass(model)}
                onClick={() => handleSort(model.id)}
              >
                <div className="flex flex-col items-center">
                  <span className={`text-center text-sm ${isCompanyModel(model.id) ? 'font-bold text-blue-700' : 'font-semibold text-gray-600'}`}>
                    {isCompanyModel(model.id) ? '★ ' : ''}{model.name}{isCompanyModel(model.id) ? ' ★' : ''}
                  </span>
                  {sortConfig.key === model.id && (
                    <span className="ml-1">{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                  )}
                </div>
              </th>
            ))}
          </tr>
          
          {/* Average scores row */}
          <tr className="bg-white font-bold">
            <td className="sticky left-0 bg-white px-2 py-2 text-xs whitespace-nowrap z-10 border-r"></td>
            <td className="sticky left-8 bg-white px-2 py-2 text-xs font-medium z-10 border-r">Average</td>
            {visibleModels.map((model) => {
              const roundedAvg = Math.round(Number(averageScores[model.id]));
              return (
                <td 
                  key={`avg-${model.id}`} 
                  className={`px-2 py-2 text-center text-sm ${isCompanyModel(model.id) ? 'bg-blue-50 border-x-2 border-blue-400' : 'bg-white'}`}
                >
                  <span className={`text-base ${isCompanyModel(model.id) ? 'font-bold text-blue-700' : 'text-gray-700'}`}>
                    {roundedAvg}%
                  </span>
                </td>
              );
            })}
          </tr>
        </thead>
        
        <tbody className="divide-y divide-gray-200">
          {filteredData.map((item) => (
            <tr key={item.id} className="hover:bg-gray-50">
              <td className="sticky left-0 bg-white px-2 py-2 text-xs whitespace-nowrap z-10 border-r">{item.id}</td>
              <td className="sticky left-8 bg-white px-2 py-2 text-xs font-medium z-10 border-r" title={item.task}>
                {formatTaskName(item.task)}
              </td>
              {visibleModels.map((model) => {
                const scoreKey = getScoreKey(model.id);
                return (
                  <td 
                    key={`${item.id}-${model.id}`} 
                    className={`px-2 py-2 text-center text-xs ${getScoreColor(item.scores[scoreKey])} ${isCompanyModel(model.id) ? 'border-x-2 border-blue-400' : ''}`}
                  >
                    {item.scores[scoreKey]}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DesktopTableView;
